// Template CRUD state. Every screen that needs templates uses this hook and
// nothing else — components never import the api module directly, so swapping
// localStorage for real endpoints stays invisible above this line.
//
// Deliberately plain useState/useEffect rather than redux: templates are read
// by exactly two screens (the builder and the report modal), neither of which
// needs the list to survive navigation. If a third consumer appears, lift this
// into a context provider — the hook's surface won't change.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTemplate,
  deleteTemplate as apiDeleteTemplate,
  fetchTemplates,
  fetchTemplatesForService,
  updateTemplate,
} from "../api/radiologyReportApi";
import { canonicalModality } from "../radiologyOrdersShared";

const norm = (s) => String(s || "").trim().toLowerCase();

// Modalities are compared canonically, never as typed: an order carrying the
// service master's "CT Scan" has to find a template authored as "CT".
const normModality = (s) => norm(canonicalModality(s));

// Templates an order may actually be reported with: those authored for the
// study's own modality, plus the deliberately modality-agnostic ones ("Any
// modality"). A CT study must never be offered an MRI layout.
//   • An order whose modality could not be resolved ("-") keeps the full list —
//     hiding everything would leave the radiologist with nothing to pick.
//   • keepTemplateId pins whatever an existing report was written from, so a
//     later modality change on that template can't make it vanish from its own
//     report's dropdown.
export function templatesForOrder(templates, order, keepTemplateId = "") {
  if (!templates?.length) return [];
  const modality = normModality(order?.modality);
  if (!modality || modality === "-") return templates;
  return templates.filter((t) => {
    const templateModality = normModality(t.modality);
    if (!templateModality) return true;
    if (templateModality === modality) return true;
    return Boolean(keepTemplateId) && t.template_id === keepTemplateId;
  });
}

// Suggests the template that best fits an order, so the radiologist usually
// just confirms rather than hunting a long list. Exact service name beats a
// partial one, which beats a modality-only match. Returns null when nothing
// matches — the UI then asks the user to choose rather than guessing.
export function suggestTemplate(templates, order) {
  if (!templates?.length || !order) return null;
  const service = norm(order.service);
  const modality = normModality(order.modality);

  let best = null;
  let bestScore = 0;
  templates.forEach((t) => {
    if (t.is_active === false) return;
    let score = 0;
    const services = (t.service_names || []).map(norm).filter(Boolean);
    if (services.some((s) => s === service)) score += 100;
    else if (service && services.some((s) => service.includes(s) || s.includes(service))) score += 55;
    if (modality && normModality(t.modality) === modality) score += 30;
    if (score > bestScore) {
      best = t;
      bestScore = score;
    }
  });
  return best;
}

/**
 * `medicalServiceId` switches the source of the list:
 *   set   → get_radiology_report_templates/ — only this investigation's
 *           templates, already scoped server-side (metadata only; each body is
 *           fetched on demand with fetchTemplateFileHtml)
 *   unset → the whole authoring list, which the Report Templates screen needs
 *
 * Both paths return the same normalized shape, so callers don't branch.
 */
export default function useRadiologyTemplates({
  activeOnly = false,
  medicalServiceId = null,
} = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = medicalServiceId
        ? await fetchTemplatesForService(medicalServiceId)
        : await fetchTemplates();
      if (mounted.current) setTemplates(list);
      return list;
    } catch (e) {
      if (mounted.current) {
        setTemplates([]);
        setError(e?.response?.data?.message || e?.message || "Could not load templates.");
      }
      return [];
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [medicalServiceId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(async (template) => {
    setSaving(true);
    try {
      const saved = template.template_id
        ? await updateTemplate(template.template_id, template)
        : await createTemplate(template);
      setTemplates((prev) => {
        const exists = prev.some((t) => t.template_id === saved.template_id);
        return exists
          ? prev.map((t) => (t.template_id === saved.template_id ? saved : t))
          : [...prev, saved];
      });
      return saved;
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, []);

  const remove = useCallback(async (templateId) => {
    setSaving(true);
    try {
      await apiDeleteTemplate(templateId);
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
      return true;
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, []);

  const visible = useMemo(
    () => (activeOnly ? templates.filter((t) => t.is_active !== false) : templates),
    [templates, activeOnly],
  );

  return { templates: visible, loading, error, saving, reload, save, remove };
}
